// Shared referral types and constants — safe for client and server
import { CREDITS } from "./credit-costs";

export interface ReferralCode {
  id: string;
  code: string;
  linkSlug: string;
  totalInvited: number;
  totalActivated: number;
  totalCredits: number;
}

export interface ReferralEvent {
  id: number;
  invitedEmail: string | null;
  status: string;
  rewardReferrer: number;
  rewardInvited: number;
  createdAt: string;
}

export interface ReferralDashboardData {
  code: ReferralCode;
  events: ReferralEvent[];
  milestones: MilestoneStatus[];
  nextMilestone: MilestoneDef | null;
}

export interface PartnerDashboardSummary {
  directReferrals: number;
  directActivated: number;
  secondLevelReferrals: number;
  secondLevelActivated: number;
  partnerCreditsEarned: number;
  directUsersCreditsEarned: number;
  directRevenueUsd: number;
  secondLevelRevenueUsd: number;
  estimatedPartnerCommissionUsd: number;
  estimatedDirectUsersCommissionUsd: number;
  commissionRate: number;
}

export interface PartnerReferralRow {
  userId: string;
  email: string | null;
  status: string;
  joinedAt: string | null;
  rewardedAt: string | null;
  partnerCreditsFromUser: number;
  referralsInvited: number;
  referralsActivated: number;
  creditsEarned: number;
  revenueUsd: number;
  estimatedCommissionUsd: number;
}

export interface PartnerSecondLevelReferralRow {
  id: number;
  directUserId: string;
  directUserEmail: string | null;
  invitedUserId: string | null;
  invitedEmail: string | null;
  status: string;
  rewardReferrer: number;
  createdAt: string | null;
}

export interface PartnerReferralTreeRow {
  eventId: number;
  level: 1 | 2;
  broughtByUserId: string;
  broughtByEmail: string | null;
  userId: string | null;
  email: string | null;
  referralStatus: string;
  joinedAt: string | null;
  currentPlan: string | null;
  subscriptionStatus: string | null;
  billingInterval: string | null;
  subscriptionPayments: number;
  paidMonths: number;
  totalPaidUsd: number;
  firstPaidAt: string | null;
  lastPaidAt: string | null;
  latestSubscriptionPack: string | null;
}

export interface PartnerDashboardData {
  summary: PartnerDashboardSummary;
  directReferrals: PartnerReferralRow[];
  recentSecondLevel: PartnerSecondLevelReferralRow[];
  referralTree: PartnerReferralTreeRow[];
}

export interface MilestoneDef {
  key: string;
  label: string;
  referralsNeeded: number;
  bonusCredits: number;
}

export interface MilestoneStatus extends MilestoneDef {
  achieved: boolean;
  achievedAt: string | null;
}

export const MILESTONES: MilestoneDef[] = [
  { key: "first_share", label: "First Share", referralsNeeded: 1, bonusCredits: CREDITS.referralMilestones.first_share },
  { key: "connector", label: "Connector", referralsNeeded: 3, bonusCredits: CREDITS.referralMilestones.connector },
  { key: "evangelist", label: "Evangelist", referralsNeeded: 5, bonusCredits: CREDITS.referralMilestones.evangelist },
  { key: "ambassador", label: "Ambassador", referralsNeeded: 10, bonusCredits: CREDITS.referralMilestones.ambassador },
  { key: "founding_circle", label: "Founding Circle", referralsNeeded: 25, bonusCredits: CREDITS.referralMilestones.founding_circle },
];

export const REWARD_REFERRER = CREDITS.referral.referrer;
export const REWARD_INVITED = CREDITS.referral.invited;
export const REFERRAL_CAP = 25; // max activated referrals per user (lifetime)
export const REF_COOKIE = "foundry_ref";
